import { Controller } from "@hotwired/stimulus"

// The cart page is platform-served and stays in dash. The cart storage API
// lives in @r2ware/sdk, which exposes it as window.R2.Cart (see the SDK's
// README / CONTRACT.md). The SDK bundle is imported by the site bundle before
// this controller registers, so window.R2.Cart is available by connect().
export default class extends Controller {
  connect() {
    const Cart = window.R2 && window.R2.Cart
    if (!Cart) {
      console.warn('R2.Cart unavailable; is @r2ware/sdk loaded on the cart page?')
      return
    }

    const showToast = (message, type = 'success') => {
      const toast = document.createElement('div');
      toast.className = `alert alert-${type} alert-dismissible fade show position-fixed`;
      toast.style.cssText = 'bottom: 20px; right: 20px; z-index: 9999; min-width: 300px;';
      toast.innerHTML = `
        ${message}
        <button type="button" class="btn-close btn-close-white" data-bs-dismiss="alert"></button>
      `;

      document.body.appendChild(toast);

      setTimeout(() => {
        if (toast.parentNode) {
          toast.classList.remove('show');
          setTimeout(() => toast.remove(), 150);
        }
      }, 3000);
    };

    const formatPrice = (cents, currency = 'usd') => {
      const dollars = cents / 100;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency.toUpperCase()
      }).format(dollars);
    };

    const renderCart = () => {
      if (!Cart.isAvailable()) {
        document.getElementById('empty-cart').innerHTML = `
          <div class="col-12">
            <div class="alert alert-danger text-center">
              <i class="bi bi-exclamation-triangle" style="font-size: 3rem;"></i>
              <h4 class="mt-3">Cart Unavailable</h4>
              <p>Your browser's localStorage is disabled. Please enable it to use the shopping cart.</p>
            </div>
          </div>
        `;
        document.getElementById('empty-cart').removeAttribute('data-js-hidden');
        return;
      }

      const items = Cart.getItems();
      const totals = Cart.getTotals();

      const cartItemsContainer = document.getElementById('cart-items');
      const emptyCart = document.getElementById('empty-cart');
      const cartSummary = document.getElementById('cart-summary');

      if (items.length === 0) {
        cartItemsContainer.innerHTML = '';
        cartItemsContainer.setAttribute('data-js-hidden', '');
        emptyCart.removeAttribute('data-js-hidden');
        cartSummary.setAttribute('data-js-hidden', '');
        return;
      }

      emptyCart.setAttribute('data-js-hidden', '');
      cartItemsContainer.removeAttribute('data-js-hidden');
      cartSummary.removeAttribute('data-js-hidden');

      const escapeHtml = (s) => String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

      const renderCustomizations = (customizations) => {
        if (!Array.isArray(customizations) || customizations.length === 0) return '';
        const lines = customizations
          .filter(c => c && c.label && c.value)
          .map(c => `<li>${escapeHtml(c.label)}: ${escapeHtml(c.value)}</li>`)
          .join('');
        return lines ? `<ul class="list-unstyled small text-muted mb-0 mt-1">${lines}</ul>` : '';
      };

      cartItemsContainer.innerHTML = items.map(item => `
        <div class="col-12 mb-3" data-line-key="${escapeHtml(item.lineKey)}">
          <div class="card">
            <div class="card-body">
              <div class="row align-items-center">
                <div class="col-md-2">
                  ${item.imageUrl ?
                    `<img src="${item.imageUrl}" alt="${item.name}" class="img-fluid rounded" style="max-height: 100px; object-fit: cover;">` :
                    `<div class="bg-light rounded d-flex align-items-center justify-content-center" style="height: 100px;">
                      <i class="bi bi-image" style="font-size: 2rem; color: #ccc;"></i>
                    </div>`
                  }
                </div>
                <div class="col-md-4">
                  <h5 class="mb-1">${item.name}</h5>
                  <p class="text-muted mb-0">${formatPrice(item.price, item.currency)} each</p>
                  ${renderCustomizations(item.customizations)}
                </div>
                <div class="col-md-3">
                  <div class="input-group">
                    <button class="btn btn-outline-secondary" type="button" data-action="decrement" data-line-key="${escapeHtml(item.lineKey)}">
                      <i class="bi bi-dash"></i>
                    </button>
                    <input type="number" class="form-control text-center" value="${item.quantity}" min="1" max="${item.maxQuantity || 99}" readonly>
                    <button class="btn btn-outline-secondary${item.quantity >= (item.maxQuantity || 99) ? ' disabled' : ''}" type="button" data-action="increment" data-line-key="${escapeHtml(item.lineKey)}"${item.quantity >= (item.maxQuantity || 99) ? ' disabled' : ''}>
                      <i class="bi bi-plus"></i>
                    </button>
                  </div>
                  ${item.quantity >= (item.maxQuantity || 99) ? `<small class="text-muted mt-1 d-block">Max ${item.maxQuantity || 99}</small>` : ''}
                </div>
                <div class="col-md-2 text-end">
                  <p class="mb-2 fw-bold">${formatPrice(item.price * item.quantity, item.currency)}</p>
                </div>
                <div class="col-md-1 text-end">
                  <button class="btn btn-link btn-sm text-danger p-0" data-action="remove" data-line-key="${escapeHtml(item.lineKey)}">
                    <i class="bi bi-x-circle"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      `).join('');

      document.getElementById('summary-item-count').textContent = totals.itemCount;
      document.getElementById('summary-subtotal').textContent = formatPrice(totals.subtotal, totals.currency);
      document.getElementById('checkout-button').disabled = false;
      renderDiscountSummary();
    };

    const renderDiscountSummary = () => {
      const totals = Cart.getTotals();
      const discount = Cart.getDiscount();
      const discountRow = document.getElementById('summary-discount-row');
      const totalEl = document.getElementById('summary-total');
      if (!discountRow || !totalEl) return;

      if (discount && discount.discountCents > 0) {
        document.getElementById('summary-discount-code').textContent = discount.code;
        document.getElementById('summary-discount-amount').textContent =
          '−' + formatPrice(discount.discountCents, totals.currency);
        discountRow.removeAttribute('data-js-hidden');
        const total = Math.max(0, totals.subtotal - discount.discountCents);
        totalEl.textContent = formatPrice(total, totals.currency);
      } else {
        discountRow.setAttribute('data-js-hidden', '');
        totalEl.textContent = formatPrice(totals.subtotal, totals.currency);
      }
    };

    const setDiscountError = (message) => {
      const el = document.getElementById('discount-error');
      if (!el) return;
      if (message) {
        el.textContent = message;
        el.removeAttribute('data-js-hidden');
      } else {
        el.textContent = '';
        el.setAttribute('data-js-hidden', '');
      }
    };

    const applyDiscount = async () => {
      const input = document.getElementById('discount-code-input');
      const button = document.getElementById('discount-apply-button');
      if (!input || !button) return;

      const code = (input.value || '').trim();
      setDiscountError('');
      if (!code) {
        setDiscountError('Enter a code.');
        return;
      }

      const original = button.innerHTML;
      button.disabled = true;
      button.innerHTML = '<span class="spinner-border spinner-border-sm"></span>';

      try {
        const items = Cart.getItems().map(i => ({
          productId: i.productId,
          quantity: i.quantity,
          customizations: i.customizations || []
        }));
        const res = await fetch('/api/shop/discount-code/validate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code, items })
        });
        const data = await res.json();
        if (!res.ok || !data.ok) {
          Cart.clearDiscount();
          setDiscountError(data.message || 'That code isn\'t valid.');
          renderDiscountSummary();
          return;
        }
        Cart.setDiscount({
          code: data.code,
          kind: data.kind,
          discountCents: data.discountCents
        });
        input.value = data.code;
        renderDiscountSummary();
      } catch (e) {
        setDiscountError('Network error. Please try again.');
      } finally {
        button.disabled = false;
        button.innerHTML = original;
      }
    };

    const removeDiscount = () => {
      Cart.clearDiscount();
      const input = document.getElementById('discount-code-input');
      if (input) input.value = '';
      setDiscountError('');
      renderDiscountSummary();
    };

    const restoreDiscountInput = () => {
      const discount = Cart.getDiscount();
      const input = document.getElementById('discount-code-input');
      if (input && discount && discount.code) {
        input.value = discount.code;
      }
    };

    const handleCartAction = (event) => {
      const button = event.target.closest('button[data-action]');
      if (!button) return;

      const action = button.dataset.action;
      const lineKey = button.dataset.lineKey;

      try {
        if (action === 'increment') {
          const items = Cart.getItems();
          const item = items.find(i => i.lineKey === lineKey);
          if (item) {
            const maxQuantity = item.maxQuantity || 99;
            if (item.quantity >= maxQuantity) {
              showToast(`<i class="bi bi-info-circle me-2"></i>Maximum quantity (${maxQuantity}) reached for <strong>${item.name}</strong>`, 'warning');
              return;
            }
            Cart.updateQuantity(lineKey, item.quantity + 1);
          }
        } else if (action === 'decrement') {
          const items = Cart.getItems();
          const item = items.find(i => i.lineKey === lineKey);
          if (item && item.quantity > 1) {
            Cart.updateQuantity(lineKey, item.quantity - 1);
          }
        } else if (action === 'remove') {
          Cart.removeItem(lineKey);
          showToast('Item removed from cart', 'info');
        }

        renderCart();
      } catch (error) {
        console.error('Error updating cart:', error);
        showToast('Error: ' + error.message, 'danger');
      }
    };

    const handleCheckout = async function() {
      const button = this;
      const originalText = button.innerHTML;

      button.disabled = true;
      button.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>Processing...';

      try {
        function generateUUID() {
          return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
          });
        }
        const checkoutSessionId = generateUUID();
        sessionStorage.setItem('checkout_session_id', checkoutSessionId);

        const items = Cart.getItems();
        const esignItems = items.filter(i => i.esignTemplateId);
        const esignDocumentId = sessionStorage.getItem('esign_document_id');

        if (esignItems.length > 0 && !esignDocumentId) {
          const templateId = esignItems[0].esignTemplateId;
          window.location.href = `/app/sign?template-id=${templateId}&shop=on`;
          return;
        }

        const payload = Cart.getCheckoutPayload();
        if (esignDocumentId) {
          payload.metadata = {
            checkout_session_id: checkoutSessionId,
            esign_document_id: esignDocumentId
          };
        }

        const response = await fetch('/api/shop/checkout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload)
        });

        const data = await response.json();

        if (!response.ok) {
          // If the discount became invalid between Apply and Checkout, drop
          // it so the next attempt doesn't keep failing on a stale code.
          if (data.error === 'discount_code_invalid' || data.error === 'discount_covers_full_amount') {
            Cart.clearDiscount();
            renderDiscountSummary();
          }
          showToast(`<i class="bi bi-exclamation-triangle me-2"></i>${data.message || 'Checkout failed'}`, 'danger');
          button.disabled = false;
          button.innerHTML = originalText;
          return;
        }

        if (data.checkoutUrl) {
          window.location.href = data.checkoutUrl;
        } else {
          showToast('<i class="bi bi-exclamation-triangle me-2"></i>Invalid checkout response', 'danger');
          button.disabled = false;
          button.innerHTML = originalText;
        }

      } catch (error) {
        console.error('Checkout error:', error);
        showToast('<i class="bi bi-exclamation-triangle me-2"></i>Network error. Please try again.', 'danger');
        button.disabled = false;
        button.innerHTML = originalText;
      }
    };

    this.renderCart = renderCart;
    this.handleCartAction = handleCartAction;
    this.handleCheckout = handleCheckout;
    this.applyDiscount = applyDiscount;
    this.removeDiscount = removeDiscount;

    renderCart();
    restoreDiscountInput();

    this.cartItemsContainer = document.getElementById('cart-items');
    if (this.cartItemsContainer) {
      this.cartItemsContainer.addEventListener('click', this.handleCartAction);
    }

    window.addEventListener('cartUpdated', this.renderCart);

    this.checkoutButton = document.getElementById('checkout-button');
    if (this.checkoutButton) {
      this.checkoutButton.addEventListener('click', this.handleCheckout);
    }

    this.discountApplyButton = document.getElementById('discount-apply-button');
    if (this.discountApplyButton) {
      this.discountApplyButton.addEventListener('click', this.applyDiscount);
    }

    this.discountRemoveButton = document.getElementById('discount-remove-button');
    if (this.discountRemoveButton) {
      this.discountRemoveButton.addEventListener('click', this.removeDiscount);
    }

    this.discountInput = document.getElementById('discount-code-input');
    if (this.discountInput) {
      this.discountInputKey = (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.applyDiscount();
        }
      };
      this.discountInput.addEventListener('keydown', this.discountInputKey);
    }
  }

  disconnect() {
    if (this.cartItemsContainer) {
      this.cartItemsContainer.removeEventListener('click', this.handleCartAction);
    }
    if (this.checkoutButton) {
      this.checkoutButton.removeEventListener('click', this.handleCheckout);
    }
    if (this.discountApplyButton) {
      this.discountApplyButton.removeEventListener('click', this.applyDiscount);
    }
    if (this.discountRemoveButton) {
      this.discountRemoveButton.removeEventListener('click', this.removeDiscount);
    }
    if (this.discountInput && this.discountInputKey) {
      this.discountInput.removeEventListener('keydown', this.discountInputKey);
    }
    window.removeEventListener('cartUpdated', this.renderCart);
  }
}
